import type { ClientCall } from "../lib/api";
import { date, dateTime, px } from "../lib/format";

interface Mark {
  key: string;
  label: string;
  price: number;
  kind: "sl" | "entry" | "target" | "exit";
}

const W = 520;
const H = 112;
const PAD = 40;
const AXIS_Y = 52;
/** Prices on the ladder drop ".00" to stay readable on a phone. */
const short = (n: number) => (Number.isInteger(n) ? n.toLocaleString("en-IN") : px(n));

/**
 * The price ladder: stop-loss, entry and targets on one line at their true distances,
 * so the risk against the reward is visible before reading a number.
 */
export function PriceLadder({ call }: { call: ClientCall }) {
  const entryLow = Math.min(call.entry.from, call.entry.to);
  const entryHigh = Math.max(call.entry.from, call.entry.to);
  const marks: Mark[] = [
    { key: "sl", label: "Stop-loss", price: call.stopLoss, kind: "sl" },
    { key: "entry", label: entryLow === entryHigh ? "Entry" : "Entry range", price: (entryLow + entryHigh) / 2, kind: "entry" },
    ...call.targets.map((t, i) => ({ key: `t${i}`, label: call.targets.length > 1 ? `Target ${i + 1}` : "Target", price: t, kind: "target" as const })),
    ...(call.exitPrice !== null ? [{ key: "exit", label: "Exit", price: call.exitPrice, kind: "exit" as const }] : []),
  ];
  const all = [...marks.map((m) => m.price), entryLow, entryHigh];
  const lo = Math.min(...all);
  const hi = Math.max(...all);
  const span = hi - lo || 1;
  const x = (p: number) => PAD + ((p - lo) / span) * (W - 2 * PAD);

  // Labels alternate rows when marks sit close together, so none overlap.
  const sorted = [...marks].sort((a, b) => x(a.price) - x(b.price));
  const lastX = [-Infinity, -Infinity];
  const rowOf = new Map<string, number>();
  for (const m of sorted) {
    const mx = x(m.price);
    const row = mx - lastX[0]! >= 96 ? 0 : 1;
    rowOf.set(m.key, row);
    lastX[row] = mx;
  }

  const colour = { sl: "var(--red)", entry: "var(--midnight)", target: "var(--green)", exit: "var(--papaya)" } as const;
  const risk = Math.abs((entryLow + entryHigh) / 2 - call.stopLoss);
  const reward = Math.abs((call.targets[0] ?? 0) - (entryLow + entryHigh) / 2);
  const ratio = risk > 0 ? reward / risk : 0;
  const summary = `Stop-loss ${px(call.stopLoss)}, entry ${px(entryLow)}${entryLow !== entryHigh ? ` to ${px(entryHigh)}` : ""}, targets ${call.targets.map(px).join(", ")}${call.exitPrice !== null ? `, exited at ${px(call.exitPrice)}` : ""}.`;

  return (
    <>
      <svg className="ladder" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={summary}>
        <line x1={PAD} x2={W - PAD} y1={AXIS_Y} y2={AXIS_Y} stroke="var(--line)" strokeWidth="2" />
        {/* risk zone and reward zone */}
        <line x1={x(call.stopLoss)} x2={x((entryLow + entryHigh) / 2)} y1={AXIS_Y} y2={AXIS_Y} stroke="var(--red)" strokeOpacity="0.35" strokeWidth="6" />
        <line
          x1={x((entryLow + entryHigh) / 2)}
          x2={x(call.targets[call.targets.length - 1] ?? entryHigh)}
          y1={AXIS_Y}
          y2={AXIS_Y}
          stroke="var(--green)"
          strokeOpacity="0.3"
          strokeWidth="6"
        />
        {entryLow !== entryHigh && <rect x={x(entryLow)} y={AXIS_Y - 7} width={Math.max(3, x(entryHigh) - x(entryLow))} height="14" rx="3" fill="var(--midnight)" />}
        {marks.map((m) => {
          const mx = x(m.price);
          const row = rowOf.get(m.key) ?? 0;
          const labelY = row === 0 ? 30 : 13;
          const priceY = row === 0 ? 82 : 102;
          const priceText = m.kind === "entry" && entryLow !== entryHigh ? `${short(entryLow)}–${short(entryHigh)}` : short(m.price);
          return (
            <g key={m.key}>
              {m.kind === "exit" ? (
                <path d={`M${mx} ${AXIS_Y - 8} l8 8 l-8 8 l-8 -8z`} fill={colour.exit} />
              ) : (
                <line x1={mx} x2={mx} y1={AXIS_Y - 9} y2={AXIS_Y + 9} stroke={colour[m.kind]} strokeWidth="3" strokeLinecap="round" />
              )}
              <text x={mx} y={labelY} textAnchor="middle" fontSize="14" fontFamily="var(--font-body)" fill="var(--slate)">
                {m.label}
              </text>
              <text x={mx} y={priceY} textAnchor="middle" fontSize="16" fontWeight="600" fontFamily="var(--font-display)" fill={colour[m.kind]}>
                {priceText}
              </text>
            </g>
          );
        })}
      </svg>
      {ratio > 0 && (
        <p className="small muted" style={{ margin: 0 }}>
          The first target is {ratio.toFixed(1)}× the distance to the stop-loss.
        </p>
      )}
    </>
  );
}

const CLOSE_TEXT: Record<NonNullable<ClientCall["closeReason"]>, { text: string; tone: "good" | "bad" | "" }> = {
  TARGET_HIT: { text: "target hit", tone: "good" },
  STOPLOSS_HIT: { text: "stop-loss hit", tone: "bad" },
  EXIT_ADVISED: { text: "exit advised", tone: "" },
  EXPIRED: { text: "expired", tone: "" },
};

const HISTORY_TEXT = { PUBLISH: "Published", UPDATE: "Updated", CLOSE: "Closed" } as const;

export function CallTicket({ call, fresh }: { call: ClientCall; fresh?: boolean }) {
  const closed = call.status === "CLOSED" && call.closeReason;
  return (
    <article className={`ticket${fresh ? " fresh" : ""}`} aria-label={`${call.side} ${call.instrument}`}>
      <div className="ticket-head">
        <h3 className="num">{call.instrument}</h3>
        <span className={`side-badge ${call.side === "SELL" ? "sell" : ""}`}>{call.side === "BUY" ? "Buy" : "Sell"}</span>
      </div>
      <p className="meta">
        {call.service}, {call.exchange}. Published {dateTime(call.publishedAt)}
        {call.horizonDays ? `, held for about ${Math.round(call.horizonDays / 7)} weeks` : ""}.
      </p>
      <PriceLadder call={call} />
      {closed && call.exitPrice !== null && (
        <p className={`outcome ${CLOSE_TEXT[call.closeReason!].tone}`} style={{ margin: "8px 0 0" }}>
          Closed at {px(call.exitPrice)}, {CLOSE_TEXT[call.closeReason!].text}
        </p>
      )}
      <details>
        <summary>Why this call, and every change ({call.history.length})</summary>
        <p style={{ marginTop: 8 }}>{call.rationale}</p>
        <ul className="history">
          {call.history.map((h) => (
            <li key={h.at}>
              <strong>{HISTORY_TEXT[h.kind]}</strong> {dateTime(h.at)}: stop-loss {px(h.stopLoss)}, targets {h.targets.map(px).join(", ")}
              {h.exitPrice !== null ? `, exit ${px(h.exitPrice)}` : ""}
              {h.note ? `. ${h.note}` : ""}
            </li>
          ))}
        </ul>
        <p className="small muted" style={{ marginBottom: 0 }}>
          Call {call.callCode}. Last change {date(call.lastUpdatedAt)}.
        </p>
      </details>
    </article>
  );
}
